import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Button,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Breadcrumbs,
  TextField,
  Alert,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { useNavigate, Link } from 'react-router-dom';
import { useGet, usePost } from '../../API/request';
import { BallTriangle } from 'react-loader-spinner';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Editor from '../../components/Editor/Editor';

const PersonalDocumentAdd = () => {
  const navigate = useNavigate();
  const getU = useGet();
  const postU = usePost();

  const [isLoaded, setIsLoaded] = useState(true);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [nothingChanged, setNothingChanged] = useState(true);

  const [cities, setCities] = useState([]);
  const [choosenCities, setChoosenCities] = useState([]);

  const [values, setValues] = useState({
    title: '',
    content: '',
    city_id: [],
    slug: '',
    seo_title: '',
    seo_description: ''
  });
  const [errors, setErrors] = useState({
    title: false,
    content: false
  });

  const [alert, setAlert] = useState({
    txt: '',
    type: 'error',
    number: 0
  });

  const showAlert = (number, type, text) => {
    setAlert({ txt: text, type, number });
    setTimeout(() => {
      setAlert({ txt: text, type, number: 0 });
      setSubmitDisabled(false);
    }, 2500);
  };

  const handleChange = (event, name) => {
    setNothingChanged(false);
    setValues({ ...values, [name]: event.target.value });
    setErrors({ ...errors, [name]: false });
  };

  const handleChangeCity = (event) => {
    const value = event.target.value;
    setChoosenCities(value);
    setValues({
      ...values,
      city_id: value.map((el) => cities.find((city) => city.name === el)?.id)
    });
  };

  const validate = () => {
    if (!values.title) {
      showAlert(3, 'error', 'Введите название документа');
      setErrors((prev) => ({ ...prev, title: true }));
      return false;
    }
    if (!values.content) {
      showAlert(3, 'error', 'Введите содержимое документа');
      setErrors((prev) => ({ ...prev, content: true }));
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (nothingChanged) {
      showAlert(1, 'error', 'Нет изменений');
      return;
    }
    if (!validate()) return;

    setSubmitDisabled(true);

    const data = {
      title: values.title,
      content: values.content,
      city_id: values.city_id
    };
    if (values.slug) data.slug = values.slug;
    if (values.seo_title) data.seo_title = values.seo_title;
    if (values.seo_description) data.seo_description = values.seo_description;

    postU('admin/v1/personal/documents', data)
      .then((resp) => {
        if (resp.status === 'success') {
          showAlert(3, 'success', 'Документ успешно добавлен');
          setTimeout(() => navigate('/app/documents'), 1500);
        } else {
          showAlert(3, 'error', 'Ошибка при добавлении документа');
        }
      })
      .catch((err) => {
        showAlert(
          3,
          'error',
          err.response?.data?.message || 'Произошла ошибка'
        );
      })
      .finally(() => {
        setSubmitDisabled(false);
      });
  };

  useEffect(() => {
    setIsLoaded(true);
    getU('city')
      .then((resp) => {
        if (resp.status === 'success') {
          setCities(resp.data.city);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoaded(false));
  }, []);

  if (isLoaded) {
    return (
      <div className="loader">
        <BallTriangle
          height="100"
          width="100"
          color="grey"
          ariaLabel="loading"
        />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Добавление текстового документа</title>
      </Helmet>
      <Box sx={{ pt: 2 }}>
        <Container maxWidth={false}>
          <Button startIcon={<ChevronLeft />} onClick={() => navigate(-1)}>
            Назад
          </Button>
          <Breadcrumbs aria-label="breadcrumb" style={{ fontSize: 14 }}>
            <Link underline="hover" color="inherit" to="/app/documents">
              Документы
            </Link>
            <p>Добавление текстового документа</p>
          </Breadcrumbs>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: 'background.default', pt: 3 }}>
        <Container maxWidth={false}>
          <Box sx={{ paddingBottom: 1 }}>
            <form>
              <Card>
                <CardHeader title="Добавление текстового документа" />
                <Divider />
                <CardContent sx={{ position: 'relative' }}>
                  <Alert
                    severity={alert.type}
                    style={{
                      display: alert.number === 1 ? 'flex' : 'none',
                      marginBottom: 8
                    }}
                  >
                    {alert.txt}
                  </Alert>
                  <TextField
                    fullWidth
                    label="Заголовок"
                    margin="normal"
                    name="title"
                    onChange={(event) => handleChange(event, 'title')}
                    type="text"
                    value={values.title}
                    variant="outlined"
                    error={errors.title}
                  />
                  <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                    <InputLabel id="cities-label">Города</InputLabel>
                    <Select
                      labelId="cities-label"
                      label="Города"
                      name="city_id"
                      multiple
                      value={choosenCities}
                      onChange={handleChangeCity}
                      input={<OutlinedInput label="Города" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {cities.map((city) => (
                        <MenuItem key={city.id} value={city.name}>
                          <Checkbox
                            checked={choosenCities.indexOf(city.name) > -1}
                          />
                          <ListItemText primary={city.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Slug"
                    margin="normal"
                    name="slug"
                    onChange={(event) => handleChange(event, 'slug')}
                    type="text"
                    value={values.slug}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="SEO заголовок"
                    margin="normal"
                    name="seo_title"
                    onChange={(event) => handleChange(event, 'seo_title')}
                    type="text"
                    value={values.seo_title}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="SEO описание"
                    margin="normal"
                    name="seo_description"
                    onChange={(event) => handleChange(event, 'seo_description')}
                    type="textarea"
                    multiline
                    value={values.seo_description}
                    variant="outlined"
                  />
                  <CardHeader title="Полное описание" />
                  <Editor
                    handleData={(data) => {
                      setValues((prev) => ({ ...prev, content: data }));
                      setNothingChanged(false);
                    }}
                    initData={values.content}
                  />
                  <Alert
                    severity={alert.type}
                    style={{
                      display: alert.number === 3 ? 'flex' : 'none',
                      marginTop: 16
                    }}
                  >
                    {alert.txt}
                  </Alert>
                </CardContent>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={submit}
                    disabled={submitDisabled}
                  >
                    Сохранить
                  </Button>
                </Box>
              </Card>
            </form>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default PersonalDocumentAdd;
